import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { analyticsService } from "../services/analyticsService";
import { screenForPath } from "../utils/analyticsScreen";
import { analyticsPath } from "../utils/analyticsUrl";
import {
  CONSENT_EVENT,
  CONSENT_KEY,
  type CookiePrefs,
  readAnalyticsConsent,
} from "../utils/cookieConsent";

/** Applies cookie consent and logs one screen view per navigation. */
export default function AnalyticsRouteTracker() {
  const location = useLocation();
  const { currentUser } = useAuth();
  const [allowed, setAllowed] = useState(readAnalyticsConsent);

  useEffect(() => {
    const apply = (enabled: boolean) => {
      setAllowed(enabled);
      analyticsService.setConsent(enabled);
    };

    const onConsent = (event: Event) => {
      const detail = (event as CustomEvent<CookiePrefs>).detail;
      if (detail && typeof detail.analytics === "boolean") {
        apply(detail.analytics);
        return;
      }
      apply(readAnalyticsConsent());
    };

    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key === CONSENT_KEY) {
        apply(readAnalyticsConsent());
      }
    };

    apply(readAnalyticsConsent());
    window.addEventListener(CONSENT_EVENT, onConsent);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(CONSENT_EVENT, onConsent);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    analyticsService.setUser(currentUser?.uid ?? null);
  }, [currentUser]);

  useEffect(() => {
    if (!allowed) return;
    const resolved = screenForPath(location.pathname);
    if (!resolved) return;
    analyticsService.trackScreen(
      resolved.screen,
      analyticsPath(resolved.pagePath, location.search),
    );
  }, [allowed, location.pathname, location.search]);

  return null;
}
